import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { FreeToBeYoungChapter1 } from '@/components/stories/free-to-be-young/chapter-1';
import { FreeToBeYoungChapter2 } from '@/components/stories/free-to-be-young/chapter-2';
import data from '@/data/free-to-be-young.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { select, t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as FreeToBeYoungData }),
  getStoryProps('free-to-be-young', 'child-power')
);

export default function FreeToBeYoung(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const answers = [
    { id: 'sixteen', label: t({ id: 'free-to-be-young.answer-1', message: '16 or below' }) },
    { id: 'seventeen', label: t({ id: 'free-to-be-young.answer-2', message: '17' }) },
    { id: 'eighteen', label: t({ id: 'free-to-be-young.answer-3', message: '18' }) },
    { id: 'nineteen', label: t({ id: 'free-to-be-young.answer-4', message: '19' }) },
    { id: 'twenty', label: t({ id: 'free-to-be-young.answer-5', message: '20' }) },
    { id: 'twentyone', label: t({ id: 'free-to-be-young.answer-6', message: '21 or above' }) },
  ];

  const sharingImage = useSharingImage('free-to-be-young');

  const pageTitle = t({ id: 'free-to-be-young.title', message: 'Free to be young' });
  const pageDescription = t({
    id: 'free-to-be-young.description',
    message:
      "In most countries around the world, you must be 18 before you can vote. But you can be legally married at a younger age in many countries — especially if you're a woman. Is that too much responsibility without enough freedom?",
  });

  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.aubergenius} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id={'free-to-be-young.theme'}>Agency</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="free-to-be-young.tagline">
              <Text type="heading">
                In most countries, you must be 18 before you can vote. But in many, you can be
                legally married at a younger age — especially if you're a woman. Is that too much
                responsibility without enough <span>freedom?</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <StoryQuestion
          question={t({
            id: 'free-to-be-young.question.question',
            message: 'What age do you think people should be allowed to vote for the first time?',
          })}
          answers={answers}
          onAnswer={handleAnswer}
          selectedAnswer={selectedAnswer}
          response={
            selectedAnswer?.id &&
            t({
              id: `free-to-be-young.answer.response`,
              message: select(selectedAnswer.id, {
                sixteen: '16 is the legal voting age in 10 countries around the world!',
                seventeen: "That's below the average legal voting age around the world!",
                eighteen: '18 is the legal voting age in most countries around the world!',
                nineteen: "That's above the average legal voting age around the world!",
                twenty: '20 is the legal voting age in 4 countries worldwide!',
                twentyone:
                  '21 is the highest legal voting age anywhere and applies in six countries!',
              }),
            })
          }
        />

        <FreeToBeYoungChapter1 data={data.people_suggested_voting_age_under_legal_voting_age} />
        <FreeToBeYoungChapter2 data={data.people_suggested_higher_than_legal_female_marriage_age} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          sharing={{
            title: pageTitle,
            text: t({
              id: 'free-to-be-young.sharing.text',
              message:
                "In most countries around the world, you must be 18 before you can vote. But you can be legally married at a younger age in many countries — especially if you're a woman. Is that too much responsibility without enough freedom?",
            }),
            url: sharingUrl,
          }}
          relatedStoryId={relatedStoryId}
        >
          <Trans id="free-to-be-young.outro-text">
            How can we enable children to{' '}
            <AnnotatedType>have a greater say in their lives?</AnnotatedType>
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
